// src/components/PushNotificationPrompt.js
//
// Small dashboard banner that lets a user enable device push notifications. If permission is
// already granted it silently refreshes the token (so a returning user keeps receiving pushes)
// and shows nothing. If not yet decided, it shows an "enable" banner (dismissible).

import React, { useEffect, useState, useCallback } from 'react';
import { FiBell, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { registerPush, listenForegroundMessages, pushSupported, showEnabledNotification } from '../utils/push';

const DISMISS_KEY = 'mam_push_prompt_dismissed';
const HAS_VAPID = !!process.env.REACT_APP_FIREBASE_VAPID_KEY;

const isDismissed = () => {
  try { return localStorage.getItem(DISMISS_KEY) === 'true'; } catch { return false; }
};
// iOS (iPhone/iPad) — including iPadOS which reports as "MacIntel" with touch.
const isIOS = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
// Running as an installed PWA (Home Screen app) rather than a normal browser tab.
const isStandalone = () =>
  window.navigator.standalone === true ||
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

export default function PushNotificationPrompt() {
  const { currentUser } = useAuth();
  const [state, setState] = useState('hidden'); // hidden | prompt | enabling | enabled | error

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentUser?.uid || !HAS_VAPID) return;

      if (!(await pushSupported())) {
        // Web push isn't available in this context. On an iPhone/iPad Safari tab this is expected —
        // iOS only allows web push for the site once it's added to the Home Screen. Show guidance
        // instead of silently hiding.
        if (isIOS() && !isStandalone() && !isDismissed() && !cancelled) setState('ios-hint');
        return;
      }

      const perm = (typeof Notification !== 'undefined' && Notification.permission) || 'default';
      if (perm === 'granted') {
        // Already allowed — refresh token silently and start foreground listener.
        listenForegroundMessages();
        registerPush(currentUser.uid);
        return;
      }
      if (perm === 'denied') return;
      // 'default' — offer the banner unless dismissed this session/device.
      if (!isDismissed() && !cancelled) setState('prompt');
    };
    run();
    return () => { cancelled = true; };
  }, [currentUser]);

  const enable = useCallback(async () => {
    if (!currentUser?.uid) return;
    setState('enabling');
    const res = await registerPush(currentUser.uid, { requestPermission: true });
    if (res.ok) {
      listenForegroundMessages();
      // Fire an immediate "notifications enabled" push so the user gets a tangible confirmation.
      showEnabledNotification();
      setState('enabled');
      setTimeout(() => setState('hidden'), 4000);
    } else {
      setState(res.reason === 'denied' ? 'hidden' : 'error');
    }
  }, [currentUser]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, 'true'); } catch { /* ignore */ }
    setState('hidden');
  };

  if (state === 'hidden') return null;

  const wrap = {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 14,
    padding: '12px 16px', marginBottom: 18,
  };

  return (
    <div style={wrap} dir="rtl">
      <FiBell style={{ color: '#357abd', fontSize: 20, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 200, color: '#1e3a8a', fontSize: 14, fontWeight: 600, lineHeight: 1.7 }}>
        {state === 'ios-hint'
          ? 'لتفعيل الإشعارات على آيفون/آيباد: افتح الموقع في Safari، اضغط زر المشاركة (□↑) ثم «إضافة إلى الشاشة الرئيسية»، وافتح التطبيق من الأيقونة الجديدة — بعدها يظهر زر التفعيل.'
          : state === 'enabled'
          ? 'تم تفعيل إشعارات الجهاز ✅ — رح توصلك المهام الجديدة على هاتفك.'
          : state === 'error'
          ? 'تعذّر تفعيل الإشعارات. جرّب من متصفّح مدعوم أو تأكّد من السماح بالإشعارات.'
          : 'فعّل إشعارات الجهاز لتصلك المهام الجديدة مباشرة على هاتفك — حتى لو الموقع مسكّر.'}
      </div>
      {(state === 'prompt' || state === 'enabling' || state === 'error') && (
        <button
          onClick={enable}
          disabled={state === 'enabling'}
          style={{
            background: 'linear-gradient(135deg, #4a90e2, #357abd)', color: '#fff', border: 'none',
            borderRadius: 10, padding: '9px 18px', fontWeight: 800, fontSize: 13.5,
            cursor: state === 'enabling' ? 'default' : 'pointer', opacity: state === 'enabling' ? 0.7 : 1,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}
        >
          {state === 'enabling' ? 'جاري التفعيل…' : 'تفعيل الإشعارات'}
        </button>
      )}
      <button
        onClick={dismiss}
        aria-label="إغلاق"
        style={{
          background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer',
          display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 8, fontSize: 16,
        }}
      >
        <FiX />
      </button>
    </div>
  );
}
